export function isJobAdmin(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('JOB_ADMIN')
}

export function isCompanyStaff(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('COMPANY_STAFF')
}

export function isStudent(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('STUDENT')
}

export function isTalentAdmin(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('TALENT_ADMIN')
}

export function isInstructor(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('INSTRUCTOR')
}

export function isCourseAdmin(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('COURSE_ADMIN')
}

export function isTestUser(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return roles.includes('TEST_USER')
}

export function getUserProfilePageUrl(userId: string, firstName: string, lastName: string): string {
  if (firstName.length > 0 && lastName.length > 0) {
    return `/user/${userId}---${toTitleSlug(`${firstName} ${lastName}`)}`
  }
  return `/user/${userId}---${toTitleSlug(firstName)}`
}

export function isClassAdmin(user: ReturnType<typeof useAuth>['data']['value']) {
  if (!user) return false
  const roles = user.user_roles || []
  return (
    roles.includes('COURSE_ADMIN') || roles.includes('TALENT_ADMIN') || roles.includes('JOB_ADMIN')
  )
}

export function getPrimaryUserRole(
  user: ReturnType<typeof useAuth>['data']['value']
): 'admin' | 'instructor' | 'employer' | 'candidate' {
  if (isJobAdmin(user) || isCourseAdmin(user) || isTalentAdmin(user) || isClassAdmin(user)) {
    return 'admin'
  }
  if (isInstructor(user)) {
    return 'instructor'
  }
  if (isCompanyStaff(user)) {
    return 'employer'
  }
  return 'candidate'
}

export function isDemoInterviewUser(email: string) {
  if (email.startsWith('demo-student-')) return true
  if (email === 'demo-anna-interview@talentlabs.org') return true
  return false
}

export function getUserManagingCompanyIdSlug(
  user: ReturnType<typeof useAuth>['data']['value'] | null
) {
  if (getPrimaryUserRole(user) !== 'employer') {
    // if the user is not a company staff, return null
    return null
  }

  // if the user is a company staff, return the companies they manage
  const manages = user?.secret_part?.managing_companies || []

  if (manages.length === 0) {
    // if the user does not manage any companies, return null
    return null
  }
  const runtimeConfig = useRuntimeConfig()
  const selectedCompany = manages.find(
    (company) => company.region_of_business === runtimeConfig.public.region_of_business && company.line_of_business === runtimeConfig.public.line_of_business
  )
  console.log('selectedCompany', selectedCompany)
  // if the user manages multiple companies, return the first one
  return selectedCompany?.id_slug
}

export function getUserInterviewIndexPage(
  user: ReturnType<typeof useAuth>['data']['value'] | null
) {
  // Return the home page of the interview feature based on the user's role and environment
  if (!user) {
    return '/login/'
  }

  const config = useRuntimeConfig()
  const primaryRole = getPrimaryUserRole(user)

  if (primaryRole === 'admin') {
    return '/dashboard/admin'
  }

  if (primaryRole === 'employer') {
    if (config.public.environmentName === 'prod') {
      alert(
        'Your company has not enabled the interview feature for candidates yet. Please contact learn@talentlabs.org for more information.'
      )
      return '/dashboard/admin'
    } else {
      // Redirect to demo interview environment for candidates for testing
      return '/dashboard/admin'
    }
  }

  if (primaryRole === 'candidate') {
    if (config.public.environmentName === 'prod') {
      alert('You cannot start your own interview, please ask the interviewer to send you the link.')
      return '/dashboard'
      //  return '/interviews/assessments/'
    } else {
      // Redirect to demo interview environment for candidates for testing
      if (isDemoInterviewUser(user.email)) {
        return '/skills/home/'
      }

      // Redirect to assessments page for candidates
      return '/skills/home/'
    }
  }

  return '/dashboard'
}

export function getHomePage(user: ReturnType<typeof useAuth>['data']['value'] | null) {
  if (!user) return '/'

  const primaryRole = getPrimaryUserRole(user)

  if (primaryRole === 'candidate') return '/dashboard'
  else return '/dashboard/admin'
}

export function isUserAccessSkills(user: ReturnType<typeof useAuth>['data']['value'] | null) {
  if (!user) return false

  const role = getPrimaryUserRole(user)
  const config = useRuntimeConfig()
  const env = config.public.environmentName

  // if this is production env
  if (env === 'prod') {
    // only allow for candidate and admin
    if (role === 'candidate' || role === 'admin') return true
    else return false
  }
  // if this is dev env
  else {
    // allow also for employers
    if (role === 'candidate' || role === 'admin' || role === 'employer') return true
    else return false
  }
}

export function isUserAccessSkillsWithAlert(
  user: ReturnType<typeof useAuth>['data']['value'] | null
) {
  if (!user) return false

  const canAccess = isUserAccessSkills(user)
  if (canAccess) return true

  const role = getPrimaryUserRole(user)

  if (role === 'employer') {
    alert(
      'Your company has not enabled the interview feature yet. Please contact learn@talentlabs.org for more information.'
    )
  } else {
    alert(
      'You are not authorized to acccess the interview feature. Please contact learn@talentlabs.org for more information.'
    )
  }

  return false
}
